import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router";
import LinkIcon from "@material-ui/icons/Link";
import StarRateIcon from "@material-ui/icons/StarRate";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CategoryIcon from "@material-ui/icons/Category";

import WorkflowCreator from "./workflow_creator";

import {
  WorkflowContext,
  WorkflowId,
  WorkflowInfo,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Switch,
} from "@material-ui/core";
import FlushAssistantsDialog from "./flush_assistants_dialog";
import {FlushAssistantsResponse} from "../containers/workflow_list";
import {
  forEachWorkflowsTreeItem,
  getSegmentedWorkflowName,
  groupWorkflowsByContextWithHierarchy,
  sortWorkflowsPreservingHierarchy,
  WorkflowTree,
} from "utils/flowmaster/workflow_grouping_utils";

type Props = {
  workflows: WorkflowInfo[];
  contexts: WorkflowContext[];
  organisationId: OrganisationId;
  addWorkflow: (name: string, isDataAsset: boolean) => void;
  updateWorkflow: (
    workflowId: WorkflowId,
    lastEdited: Date,
    data: {is_star?: boolean; is_archived?: boolean},
  ) => void;
  flushAssistants: () => Promise<FlushAssistantsResponse>;
};

const styles = {
  levelWidth: 20,
  segmentContainer: {
    display: "flex",
    alignItems: "center",
  },
  segmentName: {
    fontWeight: 600,
    color: "#9aa1a9",
    cursor: "pointer",
  },
  workflowName: {
    textDecoration: "none",
  },
  getLevelPadding: (level: number) => ({
    paddingLeft: level * 20,
  }),
  contextGroup: {
    marginBottom: "2em",
  },
  workflowItem: {
    marginBottom: "0.5em",
    display: "flex",
    alignItems: "center",
    position: "relative" as const,
  },
};

export default function WorkflowListComponent({
  workflows,
  contexts,
  organisationId,
  addWorkflow,
  updateWorkflow,
  flushAssistants,
}: Props) {
  const [
    showingFlushAssistantsDialog,
    setShowingFlushAssistantsDialog,
  ] = useState(false);
  const [showArchived, setShowArchived] = useState(() => {
    const savedValue = sessionStorage.getItem("showArchivedWorkflows");
    return savedValue ? JSON.parse(savedValue) : false;
  });
  const [viewAssetDefinitions, setViewAssetDefinitions] = useState(() => {
    const savedValue = sessionStorage.getItem("viewAssetDefinitions");
    return savedValue ? JSON.parse(savedValue) : false;
  });
  const [collapsedPaths, setCollapsedPaths] = useState<string[]>([]);

  useEffect(() => {
    sessionStorage.setItem(
      "showArchivedWorkflows",
      JSON.stringify(showArchived),
    );
  }, [showArchived]);

  useEffect(() => {
    sessionStorage.setItem(
      "viewAssetDefinitions",
      JSON.stringify(viewAssetDefinitions),
    );
  }, [viewAssetDefinitions]);

  const filteredWorkflows = useMemo(() => {
    return workflows.filter(
      workflow =>
        viewAssetDefinitions === (workflow.is_data_asset ?? false) &&
        (!workflow.is_archived || showArchived),
    );
  }, [workflows, showArchived, viewAssetDefinitions]);

  const groupedContexts = useMemo(() => {
    const contextGroups = groupWorkflowsByContextWithHierarchy(
      filteredWorkflows,
      contexts,
    ).filter(group => group.workflows.length > 0);

    contextGroups.forEach(group => {
      group.workflowsTree = sortWorkflowsPreservingHierarchy(
        group.workflowsTree,
      );
    });

    return contextGroups;
  }, [filteredWorkflows, contexts]);

  const handleToggle = (
    workflow: WorkflowInfo,
    property: "is_star" | "is_archived",
  ) => {
    updateWorkflow(workflow.id, workflow.last_edited, {
      [property]: !workflow[property],
    });
  };

  const getContextualPath = (contextId: number | string, path: string) => {
    return `context-${contextId}:${path}`;
  };

  const toggleCollapsing = (contextId: number | string, path: string) => {
    const contextualPath = getContextualPath(contextId, path);

    if (collapsedPaths.includes(contextualPath)) {
      setCollapsedPaths(collapsedPaths.filter(p => p !== contextualPath));
    } else {
      setCollapsedPaths([...collapsedPaths, contextualPath]);
    }
  };

  const renderCollapseButton = (
    contextId: number | string,
    isCollapsible: boolean,
    isCollapsed: boolean,
    path: string,
  ) => {
    if (!isCollapsible) {
      return null;
    }

    const Icon = !isCollapsed ? ExpandMoreIcon : ChevronRightIcon;

    return (
      <IconButton
        size="small"
        onClick={event => {
          event.preventDefault();
          toggleCollapsing(contextId, path);
        }}
      >
        <Icon />
      </IconButton>
    );
  };

  const renderWorkflowSegment = (
    contextId: number | string,
    name: string,
    level: number,
    path: string,
    isCollapsible: boolean,
    isCollapsed: boolean,
  ) => {
    return (
      <div
        style={{
          ...styles.segmentContainer,
          ...styles.getLevelPadding(level),
        }}
      >
        <div
          style={styles.segmentName}
          onClick={() => toggleCollapsing(contextId, path)}
        >
          {name}
        </div>
        {renderCollapseButton(contextId, isCollapsible, isCollapsed, path)}
      </div>
    );
  };

  const renderWorkflow = (workflow: WorkflowInfo, level: number) => {
    const {lastSegment} = getSegmentedWorkflowName(workflow.name);

    return (
      <div
        style={{
          ...styles.workflowItem,
          opacity: workflow.is_archived ? 0.5 : 1,
        }}
        key={workflow.id}
      >
        <div
          style={{
            ...styles.getLevelPadding(level),
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{cursor: "pointer", marginRight: "0.5em"}}
            onClick={() => handleToggle(workflow, "is_star")}
          >
            {workflow.is_star ? (
              <StarRateIcon style={{color: "#fbc02d"}} />
            ) : (
              <StarBorderIcon style={{color: "#fbc02d"}} />
            )}
          </div>
          <div
            style={{cursor: "pointer", marginRight: "0.5em"}}
            onClick={() => handleToggle(workflow, "is_archived")}
          >
            {workflow.is_archived ? (
              <UnarchiveIcon style={{color: "#3d3d3d"}} />
            ) : (
              <ArchiveIcon style={{color: "#3d3d3d"}} />
            )}
          </div>
          <Link
            to={`/organisation/${organisationId}/workflow/${workflow.id}/detail`}
            style={{
              color: "inherit",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <LinkIcon style={{marginRight: "0.5em"}} />
            {lastSegment}
          </Link>
          {workflow.is_data_asset && (
            <div style={{marginLeft: "0.5em"}}>
              <CategoryIcon />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWorkflowsTree = (contextId: number, tree: WorkflowTree) => {
    const items: Array<{
      name: string;
      workflow: WorkflowInfo | null;
      level: number;
      path: string;
      isCollapsible: boolean;
      isCollapsed: boolean;
      hasChildren: boolean;
    }> = [];

    forEachWorkflowsTreeItem(tree, (itemData, itemName, dontAddChildren) => {
      const hasChildren = Object.keys(itemData.childWorkflows).length > 0;
      const isCollapsible = hasChildren;
      const contextualPath = getContextualPath(contextId, itemData.path);
      const isCollapsed =
        isCollapsible && collapsedPaths.includes(contextualPath);

      items.push({
        name: itemName,
        workflow: itemData.workflow,
        level: itemData.level,
        path: itemData.path,
        isCollapsible,
        isCollapsed,
        hasChildren,
      });

      if (isCollapsed && dontAddChildren) {
        dontAddChildren();
      }
    });

    return items.map((item, index) => {
      if (item.workflow && !item.hasChildren) {
        return renderWorkflow(item.workflow, item.level);
      } else if (item.workflow && item.hasChildren) {
        const {lastSegment} = getSegmentedWorkflowName(item.workflow.name);

        return (
          <div key={`workflow-with-children-${index}`}>
            <div
              style={{
                ...styles.workflowItem,
                opacity: item.workflow.is_archived ? 0.5 : 1,
              }}
            >
              <div
                style={{
                  ...styles.getLevelPadding(item.level),
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{cursor: "pointer", marginRight: "0.5em"}}
                  onClick={() =>
                    handleToggle(item.workflow as WorkflowInfo, "is_star")
                  }
                >
                  {item.workflow.is_star ? (
                    <StarRateIcon style={{color: "#fbc02d"}} />
                  ) : (
                    <StarBorderIcon style={{color: "#fbc02d"}} />
                  )}
                </div>
                <div
                  style={{cursor: "pointer", marginRight: "0.5em"}}
                  onClick={() =>
                    handleToggle(item.workflow as WorkflowInfo, "is_archived")
                  }
                >
                  {item.workflow.is_archived ? (
                    <UnarchiveIcon style={{color: "#3d3d3d"}} />
                  ) : (
                    <ArchiveIcon style={{color: "#3d3d3d"}} />
                  )}
                </div>
                <div style={{display: "flex", alignItems: "center"}}>
                  <Link
                    to={`/organisation/${organisationId}/workflow/${item.workflow.id}/detail`}
                    style={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <LinkIcon style={{marginRight: "0.5em"}} />
                    {lastSegment}
                  </Link>
                  {renderCollapseButton(
                    contextId,
                    item.isCollapsible,
                    item.isCollapsed,
                    item.path,
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div key={`segment-${index}`} style={styles.workflowItem}>
            {renderWorkflowSegment(
              contextId,
              item.name,
              item.level,
              item.path,
              item.isCollapsible,
              item.isCollapsed,
            )}
          </div>
        );
      }
    });
  };

  return (
    <div style={{padding: "1em", position: "relative"}}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
        }}
      >
        <h1>WORKFLOWS</h1>
        <div>
          <Button
            style={{marginRight: "2em"}}
            variant="contained"
            onClick={() => setShowingFlushAssistantsDialog(true)}
          >
            FLUSH ALL ASSISTANTS
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchived}
                onChange={e => setShowArchived(e.target.checked)}
                color="primary"
              />
            }
            label="Show archived"
          />
          <FormControlLabel
            control={
              <Switch
                checked={viewAssetDefinitions}
                onChange={e => setViewAssetDefinitions(e.target.checked)}
                color="primary"
              />
            }
            label="View Asset Definitions"
          />
        </div>
      </div>

      {groupedContexts.length > 0 &&
        groupedContexts.map(context => (
          <div key={context.id} style={styles.contextGroup}>
            <h2>{context.name}</h2>
            {renderWorkflowsTree(context.id, context.workflowsTree)}
          </div>
        ))}
      <div style={{marginTop: "1em"}}>
        <WorkflowCreator
          addWorkflow={addWorkflow}
          createDataAsset={viewAssetDefinitions}
        />
      </div>
      <FlushAssistantsDialog
        open={showingFlushAssistantsDialog}
        onClose={() => setShowingFlushAssistantsDialog(false)}
        onFlushAssistants={flushAssistants}
      />
    </div>
  );
}
